/**
 * Card, page head, definition list and the three message boxes — the prototype's `.card`,
 * `.page-head`, `.dl`, `.warn-box`, `.ok-box` and `.info-box`.
 */
import type { HTMLAttributes, ReactElement, ReactNode } from 'react';

export function Card({
  children,
  className,
  ...rest
}: { children: ReactNode; className?: string } & HTMLAttributes<HTMLDivElement>): ReactElement {
  return (
    <div className={['card', className ?? ''].filter(Boolean).join(' ')} {...rest}>
      {children}
    </div>
  );
}

export function CardHead({
  title,
  meta,
  actions,
  headingLevel = 2,
  id,
}: {
  title: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  headingLevel?: 2 | 3;
  id?: string;
}): ReactElement {
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  return (
    <div className="card-head">
      <div>
        <Heading id={id}>{title}</Heading>
        {meta ? <span className="small muted">{meta}</span> : null}
      </div>
      {actions ? <div className="row-gap">{actions}</div> : null}
    </div>
  );
}

export function CardPad({ children, className }: { children: ReactNode; className?: string }): ReactElement {
  return <div className={['card-pad', className ?? ''].filter(Boolean).join(' ')}>{children}</div>;
}

export function CardFoot({ children }: { children: ReactNode }): ReactElement {
  return <div className="card-foot">{children}</div>;
}

export function Divider(): ReactElement {
  return <div className="divider" />;
}

export function Eyebrow({ children, tone }: { children: ReactNode; tone?: 'red' | 'yellow' }): ReactElement {
  return <span className={tone === 'yellow' ? 'eyebrow yellow' : 'eyebrow'}>{children}</span>;
}

export function PageHead({
  eyebrow,
  title,
  lead,
  actions,
  back,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  lead?: ReactNode;
  actions?: ReactNode;
  back?: ReactNode;
}): ReactElement {
  return (
    <div className="page-head">
      <div>
        {back}
        {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
        <h1>{title}</h1>
        {lead ? <p className="lead">{lead}</p> : null}
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </div>
  );
}

export function DefinitionList({
  items,
  className,
}: {
  items: { term: ReactNode; value: ReactNode; key?: string }[];
  className?: string;
}): ReactElement {
  return (
    <dl className={['dl', className ?? ''].filter(Boolean).join(' ')}>
      {items.map((item, index) => (
        <div key={item.key ?? index} style={{ display: 'contents' }}>
          <dt>{item.term}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export type BoxTone = 'warn' | 'ok' | 'info';

const BOX_CLASS: Record<BoxTone, string> = {
  warn: 'warn-box',
  ok: 'ok-box',
  info: 'info-box',
};

/**
 * Message box. `role="alert"` only for the warning tone, so a success note does not interrupt a
 * screen reader mid-task; status colour is never the only signal because the text carries it too.
 */
export function Box({
  tone,
  children,
  className,
}: {
  tone: BoxTone;
  children: ReactNode;
  className?: string;
}): ReactElement {
  return (
    <div
      className={[BOX_CLASS[tone], className ?? ''].filter(Boolean).join(' ')}
      role={tone === 'warn' ? 'alert' : undefined}
    >
      {children}
    </div>
  );
}

export function KpiCard({
  eyebrow,
  value,
  sub,
}: {
  eyebrow: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
}): ReactElement {
  return (
    <div className="kpi">
      <Eyebrow>{eyebrow}</Eyebrow>
      <span className="value tnum">{value}</span>
      {sub ? <span className="sub">{sub}</span> : null}
    </div>
  );
}
